import React, { useEffect, useRef, useState } from 'react';
import { Database, Code, FileText, Zap, Server, GitBranch, Activity, CheckCircle } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const HowOnePlaceWorksSection = () => {
  const [sectionRef, sectionVisible] = useScrollAnimation({ threshold: 0.1 });
  const [hoveredElement, setHoveredElement] = useState(null);

  // Define relationships between elements
  const relationships = {
    // Data sources only connect to agents through their MCP servers
    databases: ['nlq-tool', 'db-tools', 'enterprise-mcp', 'analytics-mcp', 'sales-agent', 'analytics-agent'],
    apis: ['api-tools', 'enterprise-mcp', 'support-mcp', 'sales-agent', 'support-agent'],
    kb: ['rag-tools', 'analytics-mcp', 'support-mcp', 'analytics-agent', 'support-agent'],
    // Tools connect to their data sources, MCP servers that use them, and agents served by those MCPs
    'nlq-tool': ['databases', 'enterprise-mcp', 'analytics-mcp', 'sales-agent', 'analytics-agent'],
    'db-tools': ['databases', 'enterprise-mcp', 'analytics-mcp', 'sales-agent', 'analytics-agent'],
    'api-tools': ['apis', 'enterprise-mcp', 'support-mcp', 'sales-agent', 'support-agent'],
    'rag-tools': ['kb', 'analytics-mcp', 'support-mcp', 'analytics-agent', 'support-agent'],
    // MCP servers connect to their tools, data sources (through tools), and their specific agent
    'enterprise-mcp': ['databases', 'apis', 'nlq-tool', 'db-tools', 'api-tools', 'sales-agent'],
    'analytics-mcp': ['databases', 'kb', 'nlq-tool', 'db-tools', 'rag-tools', 'analytics-agent'],
    'support-mcp': ['apis', 'kb', 'api-tools', 'rag-tools', 'support-agent'],
    // Agents connect to their MCP server, tools in that MCP, and data sources feeding those tools
    'sales-agent': ['databases', 'apis', 'nlq-tool', 'db-tools', 'api-tools', 'enterprise-mcp'],
    'support-agent': ['apis', 'kb', 'api-tools', 'rag-tools', 'support-mcp'],
    'analytics-agent': ['databases', 'kb', 'nlq-tool', 'db-tools', 'rag-tools', 'analytics-mcp']
  };

  const isHighlighted = (elementId) => {
    if (!hoveredElement) return false;
    if (hoveredElement === elementId) return true;
    return relationships[hoveredElement]?.includes(elementId) || false;
  };

  const getHighlightClass = (elementId) => {
    if (!hoveredElement) return '';
    if (isHighlighted(elementId)) return 'highlighted';
    return 'dimmed';
  };

  return (
    <section 
      id="how-oneplace-works"
      ref={sectionRef}
      className="section-spacing relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0b1216 0%, #0e1b22 100%)',
      }}
    >
      {/* Grain texture */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `url('data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" /%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noise)" /%3E%3C/svg%3E')`,
          backgroundSize: '200px 200px',
        }}
      />

      {/* Vignette */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.6) 100%)',
        }}
      />

      <div className="light-full-container relative z-10">
        <div className="light-content-container">
          <div className="max-w-7xl mx-auto">
            {/* Section Title */}
            <div className="text-center mb-16">
              <h2 className="display-large mb-4">
                How <span className="text-[#12D7E8]">OnePlace</span> Works
              </h2>
            </div>

            {/* Main Architecture Container */}
            <div className="relative mx-auto" style={{ maxWidth: '1100px' }}>
              
              {/* Observability Frame */}
              <div 
                className={`absolute inset-0 rounded-2xl pointer-events-none observability-frame ${hoveredElement ? 'highlighted-frame' : ''}`}
                style={{
                  border: '2px solid rgba(18, 215, 232, 0.4)',
                  transition: 'all 0.3s ease',
                }}
              />

              {/* Observability Metrics */}
              <div className={`absolute -top-8 right-4 flex gap-3 ${hoveredElement ? 'observability-highlighted' : ''}`}>
                <div className="metric-chip">
                  <Activity className="w-3 h-3" />
                  <span className="text-xs font-mono">Traces</span>
                </div>
                <div className="metric-chip">
                  <span className="text-xs font-mono">P95: 120ms</span>
                </div>
                <div className="metric-chip">
                  <CheckCircle className="w-3 h-3" />
                  <span className="text-xs font-mono">99.9%</span>
                </div>
                <div className="metric-chip">
                  <span className="text-xs font-mono">Audit</span>
                </div>
              </div>

              {/* Observability Caption */}
              <div className={`absolute -bottom-6 left-4 text-xs text-[rgba(255,255,255,0.5)] ${hoveredElement ? 'observability-highlighted' : ''}`}>
                <span className="text-[#12D7E8] font-semibold">OnePlace Agent Observability</span> — step traces, tool I/O evidence, audit & SLAs
              </div>

              {/* Content Padding for Frame */}
              <div className="px-8 py-12 space-y-8">

                {/* Layer 4: AGENTS */}
                <div>
                  <div className="mb-3 text-center">
                    <div className="inline-block px-4 py-1.5 rounded-full bg-[rgba(18,215,232,0.15)] border border-[rgba(18,215,232,0.3)] ribbon-badge">
                      <span className="text-sm font-semibold text-[#12D7E8]">
                        OnePlace Agent Builder<span className="ribbon-subtitle"> — compose reliable, governed agents in minutes</span>
                      </span>
                    </div>
                  </div>
                  <div className="agent-grid grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { name: 'Sales Agent', desc: 'Deterministic, explainable execution', id: 'sales-agent' },
                      { name: 'Support Agent', desc: 'Deterministic, explainable execution', id: 'support-agent' },
                      { name: 'Analytics Agent', desc: 'Deterministic, explainable execution', id: 'analytics-agent' },
                    ].map((agent, idx) => (
                      <div 
                        key={idx}
                        className={`agent-card group ${getHighlightClass(agent.id)}`}
                        onMouseEnter={() => setHoveredElement(agent.id)}
                        onMouseLeave={() => setHoveredElement(null)}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <GitBranch className="w-5 h-5 text-[#12D7E8]" />
                          <h4 className="font-semibold text-white">{agent.name}</h4>
                        </div>
                        <p className="agent-description text-xs text-[rgba(255,255,255,0.6)] mb-3">{agent.desc}</p>
                        {/* Mini workflow strip */}
                        <div className="workflow-strip flex items-center gap-1 text-[10px] text-[rgba(255,255,255,0.5)]">
                          <div className="workflow-step">Trigger</div>
                          <div className="workflow-arrow">→</div>
                          <div className="workflow-step">Think</div>
                          <div className="workflow-arrow">→</div>
                          <div className="workflow-step">Execute</div>
                          <div className="workflow-arrow">→</div>
                          <div className="workflow-step">Respond</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Layer 3: MCP SERVERS */}
                <div>
                  <div className="mb-3 text-center">
                    <div className="inline-block px-4 py-1.5 rounded-full bg-[rgba(18,215,232,0.15)] border border-[rgba(18,215,232,0.3)] ribbon-badge">
                      <span className="text-sm font-semibold text-[#12D7E8]">
                        OnePlace Tool Management System<span className="ribbon-subtitle"> — package tools into shareable MCP servers</span>
                      </span>
                    </div>
                  </div>
                  <div className="mcp-grid flex flex-wrap justify-center gap-4">
                    {[
                      { name: 'Enterprise MCP', tools: ['NLQ', 'DB', 'API'], env: 'Prod', id: 'enterprise-mcp' },
                      { name: 'Analytics MCP', tools: ['RAG', 'NLQ', 'DB'], env: 'Prod', id: 'analytics-mcp' },
                      { name: 'Support MCP', tools: ['RAG', 'API'], env: 'Staging', id: 'support-mcp' },
                    ].map((mcp, idx) => (
                      <div 
                        key={idx}
                        className={`mcp-capsule group ${getHighlightClass(mcp.id)}`}
                        onMouseEnter={() => setHoveredElement(mcp.id)}
                        onMouseLeave={() => setHoveredElement(null)}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Server className="w-4 h-4 text-[#12D7E8]" />
                          <span className="font-semibold text-sm text-white">{mcp.name}</span>
                        </div>
                        <div className="flex gap-1.5 mb-2 flex-wrap">
                          {mcp.tools.map((tool, tidx) => (
                            <span 
                              key={tidx} 
                              className="tool-chip"
                            >
                              {tool}
                            </span>
                          ))}
                        </div>
                        <div className="mcp-status-line flex items-center gap-2 text-[10px] text-[rgba(255,255,255,0.5)]">
                          <span className="status-dot bg-green-400"></span>
                          <span>Deployed</span>
                          <span className="mx-1">•</span>
                          <span className="font-mono">mcp://oneplace/...</span>
                          <span className="env-chip ml-auto">{mcp.env}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Layer 2: TOOLS */}
                <div>
                  <div className="mb-3 text-center">
                    <div className="inline-block px-4 py-1.5 rounded-full bg-[rgba(18,215,232,0.15)] border border-[rgba(18,215,232,0.3)] ribbon-badge">
                      <span className="text-sm font-semibold text-[#12D7E8]">
                        OnePlace Tool Management System<span className="ribbon-subtitle"> — create, govern, and reuse tools</span>
                      </span>
                    </div>
                  </div>
                  <div className="tools-grid grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                      { 
                        name: 'NLQ Tool', 
                        icon: <Zap className="w-5 h-5" />, 
                        desc: 'Ask in natural language; get accurate SQL and answers',
                        from: 'Databases',
                        id: 'nlq-tool'
                      },
                      { 
                        name: 'Database Tools', 
                        icon: <Database className="w-5 h-5" />, 
                        desc: 'Deterministic, governed database operations',
                        from: 'Databases',
                        id: 'db-tools'
                      },
                      { 
                        name: 'API Tools', 
                        icon: <Code className="w-5 h-5" />, 
                        desc: 'Reusable endpoints with auth & policies',
                        from: 'APIs',
                        id: 'api-tools'
                      },
                      { 
                        name: 'RAG Tools', 
                        icon: <FileText className="w-5 h-5" />, 
                        desc: 'Evaluated retrieval on trusted knowledge',
                        from: 'Knowledge Base',
                        id: 'rag-tools'
                      },
                    ].map((tool, idx) => (
                      <div 
                        key={idx}
                        className={`tool-card group ${getHighlightClass(tool.id)}`}
                        onMouseEnter={() => setHoveredElement(tool.id)}
                        onMouseLeave={() => setHoveredElement(null)}
                      >
                        <div className="flex items-center gap-2 mb-2 text-[#12D7E8]">
                          {tool.icon}
                          <h4 className="font-semibold text-sm text-white">{tool.name}</h4>
                        </div>
                        <p className="tool-description text-xs text-[rgba(255,255,255,0.6)] leading-relaxed">
                          {tool.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Layer 1: DATA SOURCES */}
                <div>
                  {/* Subtitle above Data Sources */}
                  <div className="text-center mb-4 text-sm text-[rgba(255,255,255,0.6)]">
                    Structured, unstructured, and API data
                  </div>
                  
                  <div className="datasources-grid grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Column A: Databases */}
                    <div 
                      className={`data-source-column ${getHighlightClass('databases')}`}
                      onMouseEnter={() => setHoveredElement('databases')}
                      onMouseLeave={() => setHoveredElement(null)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Database className="w-5 h-5 text-[#12D7E8]" />
                        <h4 className="font-semibold text-white">Databases</h4>
                      </div>
                      <div className="datasource-chips flex flex-wrap gap-1.5">
                        {['PostgreSQL', 'BigQuery', 'MySQL', 'Oracle', 'MSSQL'].map((db, idx) => (
                          <div 
                            key={idx}
                            className="db-logo-chip"
                            title={db}
                          >
                            {db}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Column B: APIs */}
                    <div 
                      className={`data-source-column ${getHighlightClass('apis')}`}
                      onMouseEnter={() => setHoveredElement('apis')}
                      onMouseLeave={() => setHoveredElement(null)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Code className="w-5 h-5 text-[#12D7E8]" />
                        <h4 className="font-semibold text-white">APIs</h4>
                      </div>
                      <div className="datasource-chips text-sm text-[rgba(255,255,255,0.6)] mb-2">
                        Enterprise & Third-Party
                      </div>
                      <div className="datasource-chips api-icon-box-thin">
                        <Code className="w-4 h-4" />
                      </div>
                    </div>

                    {/* Column C: Knowledge Base */}
                    <div 
                      className={`data-source-column ${getHighlightClass('kb')}`}
                      onMouseEnter={() => setHoveredElement('kb')}
                      onMouseLeave={() => setHoveredElement(null)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-[#12D7E8]" />
                        <h4 className="font-semibold text-white">Knowledge Base</h4>
                      </div>
                      <div className="datasource-chips flex gap-1.5">
                        {['PDF', 'DOCX', 'TXT'].map((type, idx) => (
                          <div 
                            key={idx}
                            className="file-type-chip"
                          >
                            .{type.toLowerCase()}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .metric-chip {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 12px;
          background: rgba(18, 215, 232, 0.1);
          border: 1px solid rgba(18, 215, 232, 0.2);
          color: rgba(255, 255, 255, 0.8);
          font-variant-numeric: tabular-nums;
        }

        .agent-card {
          padding: 16px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }

        .agent-card:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(18, 215, 232, 0.4);
          transform: translateY(-2px);
        }

        .workflow-step {
          padding: 2px 6px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
          white-space: nowrap;
        }

        .workflow-arrow {
          opacity: 0.5;
        }

        .mcp-capsule {
          padding: 14px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.12);
          min-width: 200px;
          transition: all 0.3s ease;
        }

        .mcp-capsule:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(18, 215, 232, 0.4);
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(18, 215, 232, 0.15);
        }

        .tool-chip {
          padding: 2px 8px;
          background: rgba(18, 215, 232, 0.15);
          border: 1px solid rgba(18, 215, 232, 0.3);
          border-radius: 6px;
          font-size: 10px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          box-shadow: 0 0 6px currentColor;
        }

        .env-chip {
          padding: 2px 6px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          font-size: 9px;
        }

        .tool-card {
          padding: 16px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }

        .tool-card:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(18, 215, 232, 0.4);
          transform: translateY(-2px);
        }

        .data-source-column {
          padding: 14px 16px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .db-logo-chip {
          padding: 4px 10px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          font-size: 10px;
          color: rgba(255, 255, 255, 0.7);
          transition: all 0.2s ease;
        }

        .db-logo-chip:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(18, 215, 232, 0.3);
          color: rgba(255, 255, 255, 0.9);
        }

        .api-icon-box-thin {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          color: rgba(255, 255, 255, 0.5);
          transition: all 0.2s ease;
          padding: 6px 12px;
          width: fit-content;
        }

        .api-icon-box-thin:hover {
          background: rgba(255, 255, 255, 0.08);
          color: rgba(18, 215, 232, 0.8);
        }

        .file-type-chip {
          padding: 5px 12px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .file-type-chip:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(18, 215, 232, 0.3);
          color: rgba(255, 255, 255, 0.9);
        }

        /* Hover highlight states */
        .highlighted {
          position: relative;
          transition: all 0.3s ease;
          box-shadow: 0 0 20px rgba(18, 215, 232, 0.5), 0 0 40px rgba(18, 215, 232, 0.3) !important;
          border-color: rgba(18, 215, 232, 0.8) !important;
          background: rgba(18, 215, 232, 0.08) !important;
          z-index: 2;
        }

        .dimmed {
          opacity: 0.3;
          transition: all 0.3s ease;
        }

        .data-source-column,
        .tool-card,
        .mcp-capsule,
        .agent-card {
          cursor: pointer;
          transition: all 0.3s ease;
        }

        /* Observability frame highlighting */
        .observability-frame {
          transition: all 0.3s ease;
        }

        .highlighted-frame {
          border-color: rgba(18, 215, 232, 0.9) !important;
          box-shadow: 
            0 0 30px rgba(18, 215, 232, 0.4),
            0 0 60px rgba(18, 215, 232, 0.2),
            inset 0 0 30px rgba(18, 215, 232, 0.1) !important;
        }

        .observability-highlighted {
          transition: all 0.3s ease;
        }

        .observability-highlighted .metric-chip {
          background: rgba(18, 215, 232, 0.2) !important;
          border-color: rgba(18, 215, 232, 0.5) !important;
          color: rgba(255, 255, 255, 1) !important;
          box-shadow: 0 0 10px rgba(18, 215, 232, 0.3);
        }

        .observability-highlighted {
          color: rgba(18, 215, 232, 1) !important;
        }

        /* Tablet and Mobile - Horizontal stacking for compactness */
        @media (max-width: 1024px) {
          /* Ribbons - thinner and no subtitles */
          .ribbon-badge {
            padding: 6px 12px !important;
          }
          
          .ribbon-badge span {
            font-size: 11px !important;
          }
          
          .ribbon-subtitle {
            display: none !important;
          }
          
          /* Observability metrics - smaller and repositioned */
          .absolute.-top-8 {
            top: -32px !important;
            right: 8px !important;
          }
          
          .absolute.-bottom-6 {
            bottom: -28px !important;
            left: 8px !important;
          }
          
          .metric-chip {
            padding: 3px 6px !important;
            font-size: 9px !important;
            gap: 3px !important;
          }
          
          .metric-chip svg {
            width: 10px !important;
            height: 10px !important;
          }
          /* Agents - horizontal layout */
          .agent-grid {
            display: flex !important;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
            gap: 12px;
          }
          
          .agent-card {
            padding: 10px 14px;
            display: inline-flex;
            align-items: center;
            min-width: auto;
            margin-bottom: 0 !important;
          }
          
          .agent-card .agent-description,
          .agent-card .workflow-strip {
            display: none !important;
          }
          
          .agent-card .flex.items-center {
            margin-bottom: 0 !important;
          }
          
          .agent-card h4 {
            font-size: 13px;
            margin: 0;
          }
          
          /* MCP Servers - more compact horizontal */
          .mcp-grid {
            gap: 10px;
          }
          
          .mcp-capsule {
            padding: 8px 12px;
            min-width: auto;
          }
          
          .mcp-capsule .mcp-status-line {
            display: none !important;
          }
          
          .mcp-capsule .flex.items-center:first-child {
            margin-bottom: 6px !important;
          }
          
          .mcp-capsule .flex.gap-1\.5 {
            margin-bottom: 0 !important;
          }
          
          /* Tools - horizontal compact */
          .tools-grid {
            display: flex !important;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
            gap: 10px;
          }
          
          .tool-card {
            padding: 8px 12px;
            flex: 0 1 auto;
            min-width: auto;
          }
          
          .tool-card .tool-description {
            display: none !important;
          }
          
          .tool-card .flex.items-center {
            margin-bottom: 0 !important;
          }
          
          .tool-card h4 {
            font-size: 12px;
          }
          
          /* Data Sources - horizontal compact */
          .datasources-grid {
            display: flex !important;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
            gap: 10px;
          }
          
          .data-source-column {
            padding: 8px 12px;
            flex: 0 1 auto;
            min-width: auto;
          }
          
          .data-source-column .datasource-chips {
            display: none !important;
          }
          
          .data-source-column .flex.items-center {
            margin-bottom: 0 !important;
          }
          
          /* Data sources - reduce padding */
          .data-source-column {
            padding: 12px;
          }
        }

        @media (max-width: 768px) {
          /* Further compression for mobile */
          .ribbon-badge {
            padding: 5px 10px !important;
          }
          
          .ribbon-badge span {
            font-size: 10px !important;
          }
          
          /* Observability metrics - even smaller */
          .absolute.-top-8 {
            top: -28px !important;
            right: 6px !important;
          }
          
          .absolute.-bottom-6 {
            bottom: -24px !important;
            left: 6px !important;
            font-size: 9px !important;
          }
          
          .metric-chip {
            padding: 2px 5px !important;
            font-size: 8px !important;
            gap: 2px !important;
          }
          
          .metric-chip svg {
            width: 8px !important;
            height: 8px !important;
          }
          
          .agent-card {
            padding: 8px 12px;
          }
          
          .agent-card h4 {
            font-size: 12px;
          }
          
          .agent-card .flex.items-center {
            gap: 6px;
          }
          
          .mcp-capsule {
            padding: 6px 10px;
            margin-bottom: 0;
          }
          
          .mcp-capsule h4,
          .mcp-capsule span {
            font-size: 11px;
          }
          
          .mcp-capsule .tool-chip {
            font-size: 9px;
            padding: 2px 6px;
          }
          
          .tool-card {
            padding: 6px 10px;
            margin-bottom: 0;
          }
          
          .tool-card h4 {
            font-size: 11px;
          }
          
          .data-source-column {
            padding: 6px 10px;
          }
          
          .data-source-column h4 {
            font-size: 11px;
          }
        }
      `}</style>
    </section>
  );
};

export default HowOnePlaceWorksSection;
